<template>
  <div class="premium-loader-overlay">
    <div class="loader-container">
      <div class="loader-main">
        <div class="loader-ring ring-1"></div>
        <div class="loader-ring ring-2"></div>
        <div class="loader-ring ring-3"></div>
        <div class="loader-center">
          <div class="loader-logo">
            <div class="logo-icon">
              <img src="@/assets/icons/OhYesMainLogo.png" alt="OhYes Logo" class="custom-logo" />
            </div>
          </div>
        </div>
      </div>
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progress + '%' }"></div>
        </div>
        <div class="progress-text">{{ Math.round(progress) }}%</div>
      </div>
      <div class="loading-text">
        <span class="text-line">{{ loadingTexts[currentTextIndex] }}</span>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
      <div class="particles">
        <div class="particle" v-for="n in 12" :key="n" :style="getParticleStyle(n)"></div>
      </div>
    </div>
    <div class="bg-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
      <div class="shape shape-4"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PremiumLoader',
  data() {
    return {
      progress: 0,
      currentTextIndex: 0,
      loadingTexts: [
        'Loading your plan details',
        'Preparing amazing offers',
        'Almost there',
        'Ready to go!',
      ],
    };
  },
  mounted() {
    this.startProgressAnimation();
    this.startTextRotation();
  },
  methods: {
    startProgressAnimation() {
      const duration = 1500;
      const interval = 30;
      const baseIncrement = 100 / (duration / interval);
      const progressTimer = setInterval(() => {
        const randomBoost = Math.random() * 3 + 1;
        this.progress += baseIncrement + randomBoost;
        if (this.progress >= 100) {
          this.progress = 100;
          clearInterval(progressTimer);
          setTimeout(() => {
            this.$emit('loading-complete');
          }, 200);
        }
      }, interval);

      setTimeout(() => {
        if (this.progress < 20) {
          this.progress = 20;
        }
      }, 100);
    },

    startTextRotation() {
      setInterval(() => {
        this.currentTextIndex = (this.currentTextIndex + 1) % this.loadingTexts.length;
      }, 1000);
    },

    getParticleStyle(index) {
      const delay = index * 0.15;
      const size = Math.random() * 8 + 4;
      const left = Math.random() * 100;
      const duration = Math.random() * 2 + 1.5;

      return {
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        width: `${size}px`,
        height: `${size}px`,
      };
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;900&display=swap');

.premium-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  font-family: 'Nunito Sans', sans-serif;
  opacity: 1;
  visibility: visible;
}

.loader-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  animation: fadeIn 0.3s ease-out;
}

.loader-main {
  position: relative;
  width: 120px;
  height: 120px;
}

.loader-ring {
  position: absolute;
  border-radius: 50%;
  border: 3px solid transparent;
  animation: rotate 1.5s linear infinite;
}

.ring-1 {
  width: 120px;
  height: 120px;
  border-top-color: #f46523;
  border-right-color: #f46523;
  animation-duration: 1.5s;
}

.ring-2 {
  width: 90px;
  height: 90px;
  top: 15px;
  left: 15px;
  border-top-color: #ff9800;
  border-left-color: #ff9800;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring-3 {
  width: 60px;
  height: 60px;
  top: 30px;
  left: 30px;
  border-top-color: #ffa726;
  border-bottom-color: #ffa726;
  animation-duration: 0.8s;
}

.loader-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-logo {
  animation: logoFloat 2s ease-in-out infinite;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 4px;
  background: rgba(244, 101, 35, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.custom-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(244, 101, 35, 0.3));
  transition: all 0.4s ease;
  animation: logoGlow 1.5s ease-in-out infinite alternate;
}

/* Progress Bar */
.progress-container {
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(244, 101, 35, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #f46523, #ff9800, #ffa726);
  border-radius: 2px;
  transition: width 0.2s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shimmer 1s infinite;
}

.progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #475569;
}

.text-line {
  animation: fadeInOut 2s ease-in-out infinite;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f46523;
  animation: bounce 1s ease-in-out infinite both;
}

.dot:nth-child(2) {
  animation-delay: 0.12s;
}

.dot:nth-child(3) {
  animation-delay: 0.24s;
}

/* Particles */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  background: linear-gradient(135deg, #f46523, #ff9800);
  border-radius: 50%;
  opacity: 0.6;
  animation: float-up 3s ease-in-out infinite;
}

.bg-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.shape {
  position: absolute;
  opacity: 0.03;
}

.shape-1 {
  width: 200px;
  height: 200px;
  background: #f46523;
  border-radius: 50%;
  top: 10%;
  right: 10%;
  animation: float 6s ease-in-out infinite;
}

.shape-2 {
  width: 0;
  height: 0;
  border-left: 100px solid transparent;
  border-right: 100px solid transparent;
  border-bottom: 180px solid #ff9800;
  bottom: 10%;
  left: 10%;
  animation: float 8s ease-in-out infinite reverse;
}

.shape-3 {
  width: 150px;
  height: 150px;
  background: #ffa726;
  transform: rotate(45deg);
  top: 20%;
  left: 5%;
  animation: rotate 10s linear infinite;
}

.shape-4 {
  width: 120px;
  height: 120px;
  background: #f46523;
  border-radius: 20px;
  bottom: 20%;
  right: 5%;
  animation: float 9s ease-in-out infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes logoFloat {
  0%,
  100% {
    transform: scale(1) translateY(0px);
  }
  50% {
    transform: scale(1.05) translateY(-5px);
  }
}

@keyframes logoGlow {
  0% {
    filter: drop-shadow(0 4px 12px rgba(244, 101, 35, 0.3));
  }
  100% {
    filter: drop-shadow(0 4px 20px rgba(244, 101, 35, 0.6));
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes float-up {
  0% {
    transform: translateY(100vh) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) scale(1);
    opacity: 0;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .loader-main {
    width: 100px;
    height: 100px;
  }

  .ring-1 {
    width: 100px;
    height: 100px;
  }

  .ring-2 {
    width: 75px;
    height: 75px;
    top: 12.5px;
    left: 12.5px;
  }

  .ring-3 {
    width: 50px;
    height: 50px;
    top: 25px;
    left: 25px;
  }

  .loader-center {
    width: 42px;
    height: 42px;
  }

  .custom-logo {
    width: 32px;
    height: 32px;
  }

  .progress-container {
    width: 150px;
  }

  .loading-text {
    font-size: 14px;
  }
}
</style>
